import React, {useState} from "react";
import {message} from "antd";
import {useBaseComponent} from "../base/base-component";
import {IPolicyFormData, IState} from "../page-components/policy/types/policies.types";
import {IPolicyResponse, Policy} from "../domains/models/policy";
import server from "../server";
import {
    createPolicy,
    deletePolicyDocument, fetchPolicy,
    modelMapper,
} from "../page-components/policy/action/policy-actions";
import DataLoading from "../common/loading-data";
import PolicyView from "../page-components/policy/policy.view";
import "../styles/component/policy.scss";
import {SearchParams} from "../page-components/common/search-criteria/type/search-criteria.type";
import dayjs from "dayjs";
import Locale from "../core/locale/locale";
import {publishPolicy} from "../page-components/common/document-card/action/document-card.actions";

const PolicyPage = () => {
    const [searchPayload, setSearchPayload] = useState<any>({
        status: "PUBLISHED",
    });
    const {state, updateState} = useBaseComponent<{}, IState>({}, {
        initialState: () => ({
            data: [],
            internal: {
                loading: false,
                isFormValid: true,
                pagination: {
                    pageNumber: 0,
                    pageSize: 10,
                    hasNext: false,
                    hasPrevious: false,
                    isLoading: false,
                    maxItems: 40
                }
            },
            isLoading: true
        }),
        onInit: () => onInit()
    });

    const onInit = async () => {
        updateState('isLoading', true);
        try {
            const policies: IPolicyResponse[] = await server.policy.search(0, state.internal.pagination.pageSize, searchPayload);
            updateState('data', policies);
            updateState('internal.pagination', {
                ...state.internal.pagination,
                hasNext: policies.length === state.internal.pagination.pageSize
            })
        } catch (e) {
            console.log("Error while fetching policy", e)
        } finally {
            updateState('isLoading', false);
        }
    };

    const handlePolicySearch = async (params: SearchParams) => {
        const payload: any = {
            title: params.title,
            status: params.status,
        };
        if (params.applicableFrom && Array.isArray(params.applicableFrom)) {
            const [startDate, endDate] = params.applicableFrom;
            const start = startDate ? dayjs(startDate).format(Locale.dateFormat) : "";
            const end = endDate ? dayjs(endDate).format(Locale.dateFormat) : "";
            payload.applicableFrom = {
                startDate: start || null,
                endDate: end || null
            };
        }
        setSearchPayload(payload);
        const policies = await server.policy.search(
            0,
            state.internal.pagination.pageSize,
            payload
        );
        updateState("data", policies);
    };

    async function handleReset() {
        const payload = {
            status: "PUBLISHED"
        };
        setSearchPayload(payload);
        const policies = await server.policy.search(
            0,
            state.internal.pagination.pageSize,
            payload
        );
        updateState("data", policies);
        updateState("internal.pagination", {
            ...state.internal.pagination,
            pageNumber: 0,
            hasNext: policies.length === state.internal.pagination.pageSize,
            hasPrevious: false
        });
    }

    const handleUnpublishPolicy = async (
        policyId: string,
        status: string,
        reason: string
    ) => {
        try {
            await server.policy.update(
                policyId,
                status,
                reason
            );
            const payload = {
                status: "PUBLISHED"
            };
            const policies = await server.policy.search(
                0,
                state.internal.pagination.pageSize,
                payload
            );
            updateState("data", policies);

            updateState("internal.pagination", {
                ...state.internal.pagination,
                pageNumber: 0,
                hasNext: policies.length === state.internal.pagination.pageSize,
                hasPrevious: false
            });

            message.success("Policy unpublished successfully!");
        } catch (error) {
            console.error("Error while unpublishing policy:", error);
            message.error("Failed to unpublish policy");
        }
    };

    const refreshPolicies = async () => {
        try {
            const policies = await server.policy.search(
                0,
                state.internal.pagination.pageSize,
                searchPayload
            );
            updateState("data", policies);
            updateState("internal.pagination", {
                ...state.internal.pagination,
                pageNumber: 0,
                hasNext: policies.length === state.internal.pagination.pageSize,
                hasPrevious: false
            });
        } catch (error) {
            console.error("Error while refreshing policies:", error);
        }
    };

    const handlePublishPolicy = async (policyId: string) => {
        try {
            await publishPolicy(policyId);
            await refreshPolicies();
            message.success("Policy published successfully!");
        } catch (error) {
            console.error("Error while publishing policy:", error);
            message.error("Failed to publish policy");
        }
    };

    const handleNotifyPolicy = async (policyId: string) => {
        try {
            await server.policy.notify(policyId);
            message.success("Policy notified successfully!")
            await refreshPolicies();
        } catch (e) {
            console.log("Error while notifying policy", e);
            message.error("Failed to notify policy!")
        }
    }

    async function onSubmit(formValues: IPolicyFormData) {
        let payload: Policy = modelMapper(formValues);
        let documentId = payload.documentId;
        const finalPayload = {
            ...payload,
            status: formValues.saveAsDraft ? "DRAFT" : "PUBLISHED"
        }
        try {
            const response = await createPolicy(finalPayload);
            const policyId = response.split('/').pop();
            if (!formValues.saveAsDraft) {
                const newPolicy = await fetchPolicy(policyId);
                updateState("data", [newPolicy, ...state.data]);
            }
            if (payload.notifyEmployees) {
                try {
                    await server.policy.notify(policyId);
                } catch (error) {
                    console.error("Policy created, but notification failed:", error);
                }
            }
            message.success("Policy created successfully!");
        } catch (e) {
            if (documentId) {
                await deletePolicyDocument(documentId)
                console.error("Create error", e);
                message.error("Failed to create policy");
            } else {
                console.error('error encountered', e)
            }
        }
    }

    function handleDeletedDoc(policyId: string) {
        const filteredPolicy = state.data.filter((item) => item.id !== policyId);
        updateState('data', filteredPolicy);
    }

    async function loadMoreNext() {
        const maxFiles = state.internal.pagination.maxItems;
        const currentPage = state.internal.pagination.pageNumber;
        const pageSize = state.internal.pagination.pageSize;

        updateState('internal.pagination.isLoading', true);

        try {
            const res = await server.policy.search(currentPage + 1, pageSize, searchPayload); // fetch next data
            let totalPolicies = [...state.data, ...res];

            // Sliding window: keep only last maxFiles items
            if (totalPolicies.length > maxFiles) {
                const excess = totalPolicies.length - maxFiles;
                totalPolicies = totalPolicies.slice(excess);
            }
            updateState('data.policies', totalPolicies);
            updateState('internal.pagination.hasNext', res.length === state.internal.pagination.pageSize);
            updateState('internal.pagination.hasPrevious', true);
            updateState('internal.pagination.pageNumber', currentPage + 1); // move to next page
        } catch (e) {
            console.log('Error while loading more media', e);
        } finally {
            updateState('internal.pagination.isLoading', false);
        }
    }

    async function loadPreviousMedia() {
        const {pageNumber, maxItems} = state.internal.pagination;
        const pageSize = state.internal.pagination.pageSize;

        // No previous page
        if (pageNumber <= 0) return;

        // if we never exceeded maxItems, nothing was trimmed
        if (state.data.length < maxItems) {
            updateState('internal.pagination.hasPrevious', false);
            return;
        }

        const previousPage = pageNumber - 1;

        updateState('internal.pagination.isLoading', true);

        try {
            const res = await server.policy.search(previousPage, pageSize, searchPayload);

            let totalDocs = [...res, ...state.data];

            // Sliding window only needed here
            if (totalDocs.length > maxItems) {
                totalDocs = totalDocs.slice(0, maxItems);
            }

            updateState('data.policies', totalDocs);
            updateState('internal.pagination.pageNumber', previousPage);
            updateState('internal.pagination.hasNext', true);
        } catch (e) {
            console.log('Error while loading previous media', e);
        } finally {
            updateState('internal.pagination.isLoading', false);
        }
    }

    return (
        state.isLoading ? <DataLoading title="Company Policies"/> :
            <PolicyView
                policies={state.data}
                onSubmit={onSubmit}
                handleDeletedDoc={handleDeletedDoc}
                handlePolicySearch={handlePolicySearch}
                handleReset={handleReset}
                handleUnpublishPolicy={handleUnpublishPolicy}
                handlePublishPolicy={handlePublishPolicy}
                handleNotifyPolicy={handleNotifyPolicy}
                paginationControls={
                    {
                        isLoading: state.internal.pagination.isLoading,
                        hasNext: state.internal.pagination.hasNext,
                        hasPrevious: state.internal.pagination.hasPrevious,
                        loadNext: loadMoreNext,
                        loadPrevious: loadPreviousMedia,
                    }
                }
            />
    )
}

export default PolicyPage;